import React, { useState, useEffect } from 'react'
import { Link, generatePath } from 'react-router-dom'
import { EditorState } from 'draft-js'
import Button from 'components/button'
import Menu from 'components/menu'
import Select from 'components/select'
import Toggle from 'components/toggle'
import Tooltip from 'components/tooltip'
import { IContextMenuState, IEmbedSubmission, ITable, ITableView } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { Dashboard, Delete, Spreadsheet } from 'components/icons'
import useProject from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { getProjectTables, uuidv4 } from 'helpers/utils'
import api from 'helpers/api'
import { TABLE } from 'routes'
import { groupOptionsByFolder } from 'components/sidebar/components/folders/tree'
import { DEFAULT_EMBED_HEIGHT } from 'components/spreadsheet/constants/const'
import { useOptionalDataContext } from 'components/document/contexts/data/useOptionalDataContext'
import { selectionInAtomicBlock } from 'components/editor/helpers/selection'
import { findEmbedSubmission } from './EmbeddedDashboard'

// One table view in a dashboard, as saved on the DASHBOARD entity.
export interface DashboardView {
  tableId: string
  viewId: string
  showToolbar: boolean
  showAggregate: boolean
  submissionId?: string
}

// The DASHBOARD entity's data. Keys set to undefined are removed from the entity when it's saved.
export interface DashboardEmbedData {
  views: DashboardView[]
  columns: number
  height: number
  includeInDownload?: boolean
  convertedFromResponse?: string
}

type SubmissionSettings = { enableSubmission: boolean; resetAfterSubmission: boolean }

// A view the form opens with: what's saved on the entity, plus the settings of its submission if the
// section has it.
export interface DashboardViewSeed extends DashboardView {
  submission?: SubmissionSettings
  // The view as saved, so keys this form doesn't manage ride along.
  saved?: Record<string, unknown>
}

// The form's seed for a saved view. A submissionId none of the section's submissions has (a dashboard
// pasted from another section; the save drops it) is not this view's: the view opens as a plain table,
// and turning Require submission on creates the section's own submission.
export const seedFromView = (view: DashboardView, submissions: IEmbedSubmission[]): DashboardViewSeed => {
  const submission = findEmbedSubmission(submissions, view.submissionId)
  return {
    tableId: view.tableId,
    viewId: view.viewId,
    showToolbar: view.showToolbar,
    showAggregate: view.showAggregate,
    submissionId: submission ? view.submissionId : undefined,
    submission: submission
      ? { enableSubmission: submission.enableSubmission, resetAfterSubmission: submission.resetAfterSubmission }
      : undefined,
    saved: view as unknown as Record<string, unknown>
  }
}

export interface DashboardEditData {
  configs: DashboardViewSeed[]
  columns: number
  height: number
  entityKey?: string
  includeInDownload?: boolean
}

interface ViewConfig extends DashboardViewSeed {
  id: string
  views: Array<{ name: string; publicId: string }>
  loadingViews?: boolean
  requireSubmission: boolean
  resetAfterSubmission: boolean
  resetTouched: boolean
}

interface DashboardInsertProps {
  editorState: EditorState
  // The editor's live state. Saving awaits the API, and edits made meanwhile must not be lost, so the
  // dashboard is applied to the state as it is when the calls return rather than when Save was pressed.
  getEditorState?: () => EditorState
  onChange: (nextEditorState: EditorState) => void
  modifier: (editorState: EditorState, data: DashboardEmbedData, entityKey?: string) => EditorState
  processId?: string
  sectionId?: string
}

interface AddDashboardFormProps extends DashboardInsertProps {
  onClose: () => void
  initialData?: DashboardEditData
}

// A new view shows its toolbar and aggregates, as a table embed always has.
const DEFAULT_SHOW_TOOLBAR = true
const DEFAULT_SHOW_AGGREGATE = true

// Helper function to generate unique IDs for configs
const generateConfigId = (): string => uuidv4()

// Require submission reflects whether the submission enables submission, not just whether the view
// has one; a view whose submission isn't in the section (settings unknown) counts as requiring it.
const initiallyRequired = (seed: DashboardViewSeed): boolean =>
  seed.submission ? seed.submission.enableSubmission : !!seed.submissionId

const configFromSeed = (seed: DashboardViewSeed): ViewConfig => ({
  ...seed,
  id: generateConfigId(),
  views: [],
  loadingViews: false,
  requireSubmission: initiallyRequired(seed),
  resetAfterSubmission: seed.submission ? seed.submission.resetAfterSubmission : false,
  resetTouched: false
})

// Helper function to create a new config with unique ID
const createNewConfig = (): ViewConfig =>
  configFromSeed({ tableId: '', viewId: '', showToolbar: DEFAULT_SHOW_TOOLBAR, showAggregate: DEFAULT_SHOW_AGGREGATE })

// The view a config shows once its table's views are loaded: the one it had, if the table still has it;
// else the table's view named "default"; else none.
const selectedViewId = (views: Array<{ name: string; publicId: string }>, currentViewId?: string): string => {
  if (currentViewId && views.some((view) => view.publicId === currentViewId)) return currentViewId
  return views.find((view) => view.name.toLowerCase() === 'default')?.publicId ?? ''
}

const downloadOptionFor = (value: boolean | undefined): string => {
  if (value === true) return 'always'
  if (value === false) return 'never'
  return 'section'
}

const includeInDownloadFor = (option: string): boolean | undefined => {
  if (option === 'always') return true
  if (option === 'never') return false
  return undefined
}

// The view as the entity saves it. Keys this form doesn't manage ride along from the saved view; a
// submissionId the form clears is left undefined, which the editor treats as removal.
const viewFromConfig = (config: ViewConfig, submissionId: string | undefined): DashboardView => ({
  ...config.saved,
  tableId: config.tableId,
  viewId: config.viewId,
  showToolbar: config.showToolbar,
  showAggregate: config.showAggregate,
  submissionId
})

export const AddDashboardForm: React.FC<AddDashboardFormProps> = ({
  editorState,
  getEditorState,
  onChange,
  modifier,
  onClose,
  initialData,
  processId,
  sectionId
}) => {
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()
  const dataContext = useOptionalDataContext()
  const [tables, setTables] = useState<ITable[]>()
  const [configs, setConfigs] = useState<ViewConfig[]>(
    initialData && initialData.configs.length > 0 ? initialData.configs.map(configFromSeed) : [createNewConfig()]
  )
  const [columns, setColumns] = useState<number>(initialData ? initialData.columns : 1)
  // Row height isn't set here any more — the dashboard is resized by dragging its bottom edge in the document.
  const height = initialData ? initialData.height : DEFAULT_EMBED_HEIGHT
  const [includeInDownload, setIncludeInDownload] = useState<boolean | undefined>(initialData?.includeInDownload)
  const [saving, setSaving] = useState<boolean>(false)
  const canRequireSubmission = !!processId && !!sectionId

  useEffect(() => {
    if (project.publicId !== '') {
      getProjectTables(project.publicId)
        .then((tables) => {
          setTables(tables)
        })
        .catch((e) => displayErrorMessage(e))
    }
  }, [project.publicId])

  const updateConfig = (configId: string, patch: Partial<ViewConfig>) => {
    setConfigs((prev) => prev.map((config) => (config.id === configId ? { ...config, ...patch } : config)))
  }

  const fetchTableViews = (tableId: string, configId: string, currentViewId?: string) => {
    setConfigs((prev) => prev.map((config) => (config.id === configId ? { ...config, loadingViews: true } : config)))
    api
      .getTableViews(tableId, { ignoreColumns: true })
      .then((viewResponse) => {
        const views = viewResponse.data
          .map((view: ITableView) => ({ name: view.name, publicId: view.publicId }))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        updateConfig(configId, { views, viewId: selectedViewId(views, currentViewId), loadingViews: false })
      })
      .catch(() => {
        updateConfig(configId, { views: [], viewId: '', loadingViews: false })
      })
  }

  /* -----------------------------------------------------------
   * If this dialog was opened for EDIT, each config already has
   * a tableId – fetch its views the first time we render.
   * --------------------------------------------------------- */
  useEffect(() => {
    if (!initialData) return

    configs.forEach((cfg) => {
      if (cfg.tableId && cfg.views.length === 0) {
        fetchTableViews(cfg.tableId, cfg.id, cfg.viewId)
      }
    })
  }, [initialData])

  // Mirrors a saved submission into the Document's in-memory section (no API save) so the embed and
  // this form's next open see it without a reload. A no-op outside a Document.
  const syncSectionSubmission = (submission: IEmbedSubmission) => {
    if (!dataContext || !sectionId) return
    dataContext.patchSection(
      sectionId,
      (section) => ({
        submissions: [
          ...(section.submissions ?? []).filter((s: IEmbedSubmission) => s.publicId !== submission.publicId),
          submission
        ]
      }),
      true
    )
  }

  const updateSubmission = async (submissionId: string, settings: Partial<SubmissionSettings>) => {
    const updated = await api.updateEmbedSubmission(project.publicId, processId!, sectionId!, submissionId, settings)
    // The update endpoint returns the whole section, so pick this submission out of it.
    // Ids compare case-insensitively, as the server does (an API caller may have written one in upper case).
    const saved = updated.data.submissions?.find(
      (sub: IEmbedSubmission) => sub.publicId.toLowerCase() === submissionId.toLowerCase()
    )
    if (saved) syncSectionSubmission(saved)
  }

  // The submission id a view saves with, creating or updating its submission as its settings ask.
  const resolveSubmissionId = async (config: ViewConfig): Promise<string | undefined> => {
    const existingId = config.submissionId
    // Outside a section there's nothing to manage, so whatever the view holds stays.
    if (!canRequireSubmission) return existingId
    const wasRequired = initiallyRequired(config)
    if (!config.requireSubmission) {
      // Turned off: the section's next save retires the submission nothing points at any more. One that
      // was never required (a disabled or legacy submission) is left as it is.
      return wasRequired ? undefined : existingId
    }
    const resetAfterSubmission = config.resetAfterSubmission
    if (!existingId) {
      const created = await api.createEmbedSubmission(project.publicId, processId!, sectionId!, {
        enableSubmission: true,
        resetAfterSubmission
      })
      syncSectionSubmission(created.data)
      // Held on the view straight away, so a retry after a later view fails updates it rather than
      // creating another.
      const settings = { enableSubmission: true, resetAfterSubmission }
      updateConfig(config.id, { submissionId: created.data.publicId, submission: settings, resetTouched: false })
      return created.data.publicId
    }
    if (!wasRequired) {
      // Re-enabling a submission that doesn't enable submission must say so.
      await updateSubmission(existingId, { enableSubmission: true, resetAfterSubmission })
    } else if (config.resetTouched) {
      // The submission's stored settings may be unknown here, so any touch is sent rather than diffed.
      await updateSubmission(existingId, { resetAfterSubmission })
    }
    return existingId
  }

  // Kept outside handleSubmit's try block, which the React Compiler can't compile optional chaining inside.
  // One view at a time, so a failure stops before the views after it are touched.
  const buildEmbedData = async (): Promise<DashboardEmbedData> => {
    const views: DashboardView[] = []
    for (const config of configs) {
      views.push(viewFromConfig(config, await resolveSubmissionId(config)))
    }
    return { views, columns, height, includeInDownload }
  }

  // Outside handleSubmit's try block, like buildEmbedData: the React Compiler can't compile a conditional there.
  const liveEditorState = (): EditorState => (getEditorState ? getEditorState() : editorState)

  const handleSubmit = async () => {
    // Enter in the columns field can submit again while a save is still in flight.
    if (saving) return
    if (configs.some((c) => c.tableId === '' || c.viewId === '')) {
      displayErrorMessage('Please select a table and view for each dashboard view.')
      return
    }
    const entityKey = initialData?.entityKey
    // A new dashboard can't go into an atomic block (the editor ignores the insert), so don't create
    // submissions nothing would point at.
    if (!entityKey && selectionInAtomicBlock(liveEditorState())) {
      handleClose()
      return
    }
    setSaving(true)
    try {
      const data = await buildEmbedData()
      onChange(modifier(liveEditorState(), data, entityKey))
      handleClose()
    } catch (error) {
      displayErrorMessage(error)
    }
    setSaving(false)
  }

  const handleClose = () => {
    setConfigs([createNewConfig()])
    setColumns(1)
    onClose()
  }

  const handleAddView = () => {
    setConfigs((prev) => [...prev, createNewConfig()])
  }

  const handleRemoveView = (index: number) => {
    const nextLen = configs.length - 1
    setConfigs((prev) => {
      const next = prev.filter((_, i) => i !== index)
      return next.length > 0 ? next : [createNewConfig()]
    })
    setColumns((prev) => (prev > nextLen ? Math.max(1, nextLen) : prev))
  }

  const handleKeyPressed = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSubmit()
    } else if (event.key === 'Escape') {
      handleClose()
    }
  }

  return (
    <div className="flex flex-col overflow-y-auto gap-[15px] h-[300px] py-3 px-5">
      <div className="flex gap-[15px]">
        <div className="flex flex-col w-[120px]">
          <span className="text-muted italic mb-[3px]"># Columns</span>
          <input
            type="number"
            min={1}
            max={configs.length}
            value={columns}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setColumns(Math.min(Math.max(1, Number(e.target.value) || 1), configs.length))
            }
            onKeyDown={handleKeyPressed}
          />
        </div>
        {/* Downloads are a Document's; elsewhere the setting would do nothing. */}
        {processId && (
          <div className="flex flex-col w-[220px]">
            <span className="text-muted italic mb-[3px]">Include in download</span>
            <Select
              options={[
                { value: 'section', label: 'Follow section setting' },
                { value: 'always', label: 'Always' },
                { value: 'never', label: 'Never' }
              ]}
              optionsSelected={[downloadOptionFor(includeInDownload)]}
              onOptionClick={(option) => setIncludeInDownload(includeInDownloadFor(option))}
              searchable={false}
            />
          </div>
        )}
      </div>
      {configs.map((cfg, index) => (
        <div key={cfg.id} className="flex flex-wrap items-end gap-x-[15px] gap-y-[5px]">
          <div className="flex flex-col min-w-[284px] max-w-[284px]">
            <span className="text-muted italic mb-[3px]">Select Table</span>
            <Select
              options={groupOptionsByFolder(
                (tables ?? []).map((table) => ({ value: table.publicId, label: table.name })),
                project.hubFolders,
                project.sidebarResources
              )}
              loading={tables === undefined}
              onOptionClick={(option) => {
                setConfigs((prev) =>
                  prev.map((c) => (c.id === cfg.id ? { ...c, tableId: option, viewId: '', views: [] } : c))
                )
                fetchTableViews(option, cfg.id)
              }}
              groupBy={true}
              optionsSelected={cfg.tableId ? [cfg.tableId] : []}
            />
          </div>
          <div className="flex flex-col min-w-[284px] max-w-[284px]">
            <span className="text-muted italic mb-[3px]">Select View</span>
            <Select
              options={cfg.views.map((view) => ({ value: view.publicId, label: view.name }))}
              loading={cfg.loadingViews}
              onOptionClick={(option) => {
                setConfigs((prev) => prev.map((c) => (c.id === cfg.id ? { ...c, viewId: option } : c)))
              }}
              optionsSelected={cfg.viewId ? [cfg.viewId] : []}
            />
          </div>

          <div className="flex flex-col flex-1">
            <span className="text-muted italic mb-[3px]">Show Toolbar</span>
            <Select
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' }
              ]}
              optionsSelected={[cfg.showToolbar ? 'true' : 'false']}
              onOptionClick={(option) => {
                const newValue = option === 'true'
                setConfigs((prev) => prev.map((c) => (c.id === cfg.id ? { ...c, showToolbar: newValue } : c)))
              }}
              searchable={false}
            />
          </div>

          <div className="flex flex-col flex-1">
            <span className="text-muted italic mb-[3px]">Show Aggregate</span>
            <Select
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' }
              ]}
              optionsSelected={[cfg.showAggregate ? 'true' : 'false']}
              onOptionClick={(option) => {
                const newValue = option === 'true'
                setConfigs((prev) => prev.map((c) => (c.id === cfg.id ? { ...c, showAggregate: newValue } : c)))
              }}
              searchable={false}
            />
          </div>

          <Button internalType="danger" className="h-[45px]" onClick={() => handleRemoveView(index)}>
            <Delete />
          </Button>

          <div>
            {cfg.tableId && cfg.viewId && project.publicId && (
              <div className="text-sm">
                <Link
                  to={generatePath(TABLE, { projectId: project.publicId, tableId: cfg.tableId, viewId: cfg.viewId })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click To View Selected Table View
                </Link>
              </div>
            )}
          </div>

          {canRequireSubmission && (
            <div className="flex items-center w-full gap-[15px]">
              <div className="flex items-center gap-2.5">
                <span className="text-muted italic">Require submission</span>
                <Toggle
                  aria-label="Require submission"
                  toggled={cfg.requireSubmission}
                  onToggle={(value) => updateConfig(cfg.id, { requireSubmission: value })}
                />
              </div>
              {cfg.requireSubmission && (
                <div className="flex items-center gap-2.5">
                  <span className="text-muted italic">Reset after submission</span>
                  <Toggle
                    aria-label="Reset after submission"
                    toggled={cfg.resetAfterSubmission}
                    onToggle={(value) => updateConfig(cfg.id, { resetAfterSubmission: value, resetTouched: true })}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      <Button className="w-[175px] self-start" onClick={handleAddView} internalType="outline">
        <Spreadsheet />
        <span className="ml-[5px]">Add Another View</span>
      </Button>
      <Button
        className="w-[175px] self-end"
        onClick={handleSubmit}
        internalType="accept"
        disabled={saving}
        data-testid="embed-dashboard-submit"
      >
        <Dashboard />
        <span className="ml-[5px]">{initialData ? 'Update Dashboard' : 'Add Dashboard'}</span>
      </Button>
    </div>
  )
}

interface AddIconProps {
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void
}

const AddDashboardIcon: React.FC<AddIconProps> = ({ onClick }) => {
  return (
    <Tooltip content="Add Table/Dashboard">
      <div
        className="flex text-base text-body items-center h-full cursor-pointer hover:bg-surface-sunken select-none"
        onClick={onClick}
      >
        <Dashboard className="mr-[5px]" />
        Table/Dashboard
      </div>
    </Tooltip>
  )
}

const DashboardInsert: React.FC<DashboardInsertProps> = ({
  editorState,
  getEditorState,
  onChange,
  modifier,
  processId,
  sectionId
}) => {
  const [menuState, setMenuState] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const menuWidth = 1000
  const offset = 500

  return (
    <div>
      <AddDashboardIcon
        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
          setMenuState({
            open: true,
            top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
            left: `${event.currentTarget.getBoundingClientRect().left - menuWidth + offset}px`,
            right: 'auto',
            bottom: 'auto'
          })
        }}
      />
      {menuState.open && (
        <Menu
          id="dashboard-insert-menu"
          menuState={menuState}
          setMenuState={setMenuState}
          width={menuWidth}
          zIndex={1000}
        >
          <AddDashboardForm
            editorState={editorState}
            getEditorState={getEditorState}
            onChange={onChange}
            onClose={() => setMenuState(INITIAL_CONTEXT_MENU_STATE)}
            modifier={modifier}
            processId={processId}
            sectionId={sectionId}
          />
        </Menu>
      )}
    </div>
  )
}

export default DashboardInsert
